import { useCallback, useEffect, useState } from 'react';

import { di } from 'react-magnetic-di';

import FeatureGates, {
	type ClientOptions,
	type CustomAttributes,
	FeatureGateEnvironment,
	type Identifiers,
	type OptionsWithDefaults,
	PerimeterType,
} from '@atlaskit/feature-gate-js-client';
import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags';
import { getConfig } from '@atlassian/atlassian-studio-common';
import { getMappedEnv } from '@atlassian/atlassian-studio-env-utils';
import { logException } from '@atlassian/atlassian-studio-observability';

import { initCacheFromLocalStorage, studioFg } from '../persistance';

const isProd = getMappedEnv() === 'production';
export const featureGatesOptions: OptionsWithDefaults<ClientOptions> = {
	apiKey: getConfig().featureGateApiKey,
	environment: isProd ? FeatureGateEnvironment.Production : FeatureGateEnvironment.Staging,
	targetApp: 'atlassian-studio_web',
	perimeter: PerimeterType.COMMERCIAL,
};

export const useInitialiseFeatureGatesClient = (
	identifiers: Identifiers,
	clientOptions: ClientOptions,
	customAttributes?: CustomAttributes,
): {
	initialized: boolean;
	failReason: string | null;
} => {
	di(initCacheFromLocalStorage, setBooleanFeatureFlagResolver, logException);
	const [initialized, setInitialized] = useState(false);
	const [failReason, setFailReason] = useState<string | null>(null);

	const initialize = useCallback(async () => {
		// check if supplied identifiers are all valid
		// if they are not, return early, do not initialize
		const validIdentifiers =
			Object.entries(identifiers).length === 0
				? false
				: Object.entries(identifiers).every(([_key, value]) => Boolean(value));

		// @NOTE: if any of the identifiers are invalid, do not bother initializing
		if (!validIdentifiers) {
			return;
		}

		try {
			await FeatureGates.initialize(clientOptions, identifiers, customAttributes);
			await FeatureGates.waitUntilInitializeCompleted();
			// @NOTE: prime the local-overides in-memory cache from ls-cache just once in here:
			initCacheFromLocalStorage();

			// @NOTE: patch fg() from "@atlaskit/platform-feature-flags",
			// to use the new persisted feature gate value system
			setBooleanFeatureFlagResolver(studioFg);
			setInitialized(true);
			setFailReason(null);
		} catch (error) {
			logException(error, {
				identifiers,
			});

			// oxlint-disable-next-line no-console
			console.error('🚨 Error initializing feature gates:', error);
			if (error instanceof Error) {
				setFailReason(error.message);
			}
		}
	}, [identifiers, clientOptions, customAttributes]);

	useEffect(() => {
		if (!initialized) {
			initialize();
		}
	}, [initialize, initialized]);

	return { initialized, failReason };
};
